import "../styles/Onboarding.css";
import "../styles/gdk";
import { useState, useEffect, useRef, useContext } from "react";
import { GdkLoader } from "@gdk/loader";
import { GdkCheckpoint } from "@gdk/checkpoint";
import { GdkToasts } from "@gdk/toasts";
import BankingDetails from "./BankingDetails";
import BankingAlerts from "./BankingAlerts";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/userContext";
import { getAccountNumberLastFour } from "../services/ApiServices/ApiService";
import {
  BANKING_ACCOUNT_NEEDS_UPDATING,
  BANKING_ACCOUNT_UPDATE_FAILED,
  BILLING_API_ERROR,
  DOCUSIGN_DECLINE,
  DOCUSIGN_EVENT,
  DOCUSIGN_SIGNING_COMPLETE,
} from "../utils/constants/index";
import * as rewardsApi from "../services/ApiServices/ApiService";
import { logError, logTrace } from "../services/ApiServices/LoggingService";

const getBankingCheckpointStatus = (taxOnFile, bankingOnFile) => {
  if (!bankingOnFile) {
    return taxOnFile ? "in process" : "";
  }

  return taxOnFile ? "completed" : undefined;
};

const Onboarding = () => {
  const location = useLocation();
  const user = useContext(UserContext);
  const [taxFormOnFile, setTaxFormOnFile] = useState(false);
  const [bankingOnFile, setBankingOnFile] = useState(false);
  const [accountNumberLastFour, setAccountNumberLastFour] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [editTaxMode, setEditTaxMode] = useState(false);
  const [editBankingMode, setEditBankingMode] = useState(false);

  const [taxButtonText, setTaxButtonText] = useState("Complete My W-9");
  const [toastHeader, setToastHeader] = useState("");
  const [toastDescription, setToastDescription] = useState("");

  const [showBillingApiErrorAlert, setShowBillingApiErrorAlert] =
    useState(false);

  const gdkLoader = useRef(null);
  const gdkFullPageLoader = useRef(null);
  const gdkCheckpoint = useRef(null);
  const gdkToast = useRef(null);

  const navigate = useNavigate();

  const routeToTaxForm = () => {
    if (editTaxMode) {
      navigate("/taxForm", { state: { edit: true } });
    } else {
      navigate("/taxForm");
    }
  };

  const editTax = () => {
    setEditMode(false);
    setEditTaxMode(true);
    setTaxFormOnFile(false);
    setTaxButtonText("Submit New W-9");
  };

  const editBanking = () => {
    setEditMode(false);
    setEditBankingMode(true);
    setBankingOnFile(false);
  };

  const updateAgentTaxInfoOnFile = async (change) => {
    let apiCallSuccess = false;
    let postResult;

    const handleError = (error) => {
      logTrace(error, "error when calling rewardsApi.updateAgentTaxInfoOnFile" , "Updating agent tax info");
    };

    const handleSuccess = (responseData) => {
      apiCallSuccess = true;
      postResult = responseData;
    };

    await rewardsApi.updateAgentTaxInfoOnFile(
      change,
      { data: change },
      handleSuccess,
      handleError
    );

    if (apiCallSuccess) {
      return postResult;
    }
  };

  const uploadTaxForm = async () => {
    const handleError = (error) => {
      logTrace(error, "error when calling rewardsApi.uploadTaxInfo", "Uploading tax form");
    };

    const handleSuccess = (responseData) => {};

    await rewardsApi.uploadTaxInfo(handleSuccess, handleError);
  };

  const initializeGdkCheckpoint = (
    accountNumLastFourResult,
    taxFormOnFileFromLanding,
    editModeFromNavMenu,
    bankingCheckpointStatus
  ) => {
    const success = (data) => {
      if (typeof data === "number" || (typeof data === "string" && data.length === 4)) {
        init(`Checking account ending in ${data}`);
      } else {
        init("We don't have your banking details on record yet.");
      }
    };

    const error = () => {
      init("We don't have your banking details on record yet.");
    };

    const init = (bankInfoDescription) => {
      gdkCheckpoint.current = new GdkCheckpoint({
        content: "#two-steps",
        data: [
          {
            title: "W-9 submitted electronically",
            status: taxFormOnFileFromLanding ? "completed" : "in process",
            description: editModeFromNavMenu
              ? "Your tax information is on file."
              : "This is for tax purposes.",
          },
          {
            title: "Current Payout Method On File",
            status: bankingCheckpointStatus,
            description: bankInfoDescription,
          },
        ],
        animated: false,
      });
    };

    if (accountNumLastFourResult != null) {
      success(accountNumLastFourResult);
    } else {
      error();
    }
  };

  const ensureGdkLoaders = () => {
    if (!gdkLoader.current) {
      gdkLoader.current = new GdkLoader({
        content: "#extendedWaiting",
        type: "inline",
        autoShow: false,
      });
    }

    if (!gdkFullPageLoader.current) {
      gdkFullPageLoader.current = new GdkLoader({
        content: "#full-loader",
        type: "section",
        autoShow: false,
      });
    }
  };

  const ensureGdkToast = () => {
    if (!gdkToast.current) {
      gdkToast.current = new GdkToasts({
        content: "#confirmation-toast",
        type: "update",
        autoShow: false,
        floatTime: 3000,
        closeBtn: true,
        baseTimer: true,
      });
    }
  };

  const applyEnrollmentUi = (editModeFromNavMenu) => {
    // If user directly navigates to /onboarding from Home, enrollment status must be true to access Home, so default to true
    // Otherwise, retrieve state passed from Landing page
    const taxFormOnFileFromLanding =
      editModeFromNavMenu ||
      user.enrollmentStatus.taxInfo ||
      location.state?.taxFormOnFile;

    const bankingOnFileFromLanding =
      editModeFromNavMenu ||
      user.enrollmentStatus.bankingOnFile ||
      location.state?.bankingOnFile;

    setTaxFormOnFile(taxFormOnFileFromLanding);
    setBankingOnFile(bankingOnFileFromLanding);

    if (editModeFromNavMenu) {
      setEditMode(editModeFromNavMenu);
      setEditTaxMode(false);
      setEditBankingMode(false);
    }

    const onboardingComplete =
      !editModeFromNavMenu && taxFormOnFileFromLanding && bankingOnFileFromLanding;

    let homeRedirectTimeout;
    if (onboardingComplete) {
      gdkLoader.current.show();
      homeRedirectTimeout = setTimeout(() => {
        navigate("/home");
      }, 2500);
    } else {
      gdkLoader.current.hide();
    }

    initializeGdkCheckpoint(
      accountNumberLastFour,
      taxFormOnFileFromLanding,
      editModeFromNavMenu,
      getBankingCheckpointStatus(taxFormOnFileFromLanding, bankingOnFileFromLanding)
    );

    return homeRedirectTimeout;
  };

  const showConfirmationToast = (state) => {
    if (!state) {
      return;
    }

    if (state.displayBankingToast) {
      setToastHeader("Banking Info Updated");
      setToastDescription(
        "Your banking information has been updated successfully."
      );
      gdkToast.current.show();
      return;
    }

    if (state.displayTaxToast) {
      setToastHeader("Tax Form Updated");
      setToastDescription(
        "Your tax information has been updated successfully."
      );
      gdkToast.current.show();
    }
  };

  const completeDocusignSigning = () => {
    if (user && !user.enrollmentStatus.taxInfo) {
      gdkFullPageLoader.current.show();
      updateAgentTaxInfoOnFile(true);
    }

    uploadTaxForm();
    setTimeout(() => {
      window.location.reload();
      gdkFullPageLoader.current.hide();
    }, 2000);
  };

  const handleDocusignEvent = (eventType) => {
    if (!eventType) {
      return;
    }

    if (eventType === DOCUSIGN_SIGNING_COMPLETE) {
      completeDocusignSigning();
    } else if (eventType === DOCUSIGN_DECLINE) {
      setToastHeader("Tax Form Declined");
      setToastDescription(
        "Your tax form was declined. Your information will not be updated or stored by GEICO."
      );
      gdkToast.current.show();
    } else {
      navigate("/Error");
    }

    window.history.replaceState(
      {},
      document.title,
      window.location.origin + window.location.pathname
    );
  };

  useEffect(() => {
    getAccountNumberLastFour((data) => {setAccountNumberLastFour(data?.response ?? "")}, (data) => {logError(data, "in Onboarding.jsx, in getAccountNumberLastFour")});
  }, []);

  // Toast and DocuSign must not re-run when accountNumberLastFour arrives, or confirmation toasts fire twice.
  useEffect(() => {
    ensureGdkLoaders();
    ensureGdkToast();

    const eventType = new URLSearchParams(window.location.search).get(DOCUSIGN_EVENT);
    showConfirmationToast(location.state);
    handleDocusignEvent(eventType);
  }, [location.state, user]);

  useEffect(() => {
    ensureGdkLoaders();

    if (!user) {
      return;
    }

    const homeRedirectTimeout = applyEnrollmentUi(location.state?.editMode ?? false);

    return () => {
      if (homeRedirectTimeout) {
        clearTimeout(homeRedirectTimeout);
      }
    };
  }, [location.state, user, accountNumberLastFour]);

  return (
      <div className="container">
        <div id="confirmation-toast" className="toasts-container">
          <div className="toasts toasts--confirmation">
            <div>
              <span className="icon-confirmation"></span>
            </div>
            <div role="status">
              <div className="toasts--heading">{toastHeader}</div>
              <div className="toasts--content">{toastDescription}</div>
            </div>
            <button className="toasts--close-btn icon-close">
              <div id="toasts--base-timer"></div>
            </button>
          </div>
        </div>
        {showBillingApiErrorAlert && (
          <BankingAlerts alertType={BILLING_API_ERROR} />
        )}
        {user?.enrollmentStatus?.bankingStatus ===
          BANKING_ACCOUNT_NEEDS_UPDATING && (
          <BankingAlerts alertType={BANKING_ACCOUNT_NEEDS_UPDATING} />
        )}
        {user?.enrollmentStatus?.bankingStatus ===
          BANKING_ACCOUNT_UPDATE_FAILED && (
          <BankingAlerts alertType={BANKING_ACCOUNT_UPDATE_FAILED} />
        )}
        {(editMode || editBankingMode || editTaxMode) && (
          <h2 className="centered-text" data-testid="welcome-heading">
            Manage W-9 and Banking Details
          </h2>
        )}
        {!editMode && taxFormOnFile && bankingOnFile && (
          <h2 className="centered-text" data-testid="welcome-heading">
            You're all set!
          </h2>
        )}
        {!editMode &&
          !editTaxMode &&
          !editBankingMode &&
          (!taxFormOnFile || !bankingOnFile) && (
            <div data-testid="welcome-heading">
              <h2 className="centered-text">Welcome to GEICO Rewards!</h2>
              <p className="centered-text">
                We need a few more items to get you into your dashboard.
              </p>
            </div>
          )}
        <br></br>
        <div className="steps">
          <div
            id="full-loader"
            className="loader-container"
            data-testid="loader"
          >
            <div className="loader-content">
              <div className="loader loader--animated" aria-label="Please wait">
                <div className="animated-loader-copy">Loading...</div>
              </div>
            </div>
          </div>
          <div id="remaining-steps" data-testid="two-steps">
            {!editMode && <h4>2 Quick Steps:</h4>}
            <div id="two-steps" className="checkpoint no-bullet"></div>
          </div>
          <div
            id="next-step"
            className={
              "next-step " +
              (taxFormOnFile && !bankingOnFile ? "wider-flex" : "")
            }
            data-testid="next-step"
          >
            {!taxFormOnFile && (
              <div id="taxForm">
                <h3>Have one of these ready:</h3>
                <p className="closer-spacing">
                  Employer Identification Number (EIN) <b>or</b>
                </p>
                <hr className="smaller-margin"></hr>
                <p>Social Security Number (SSN)</p>
                <button
                  type="button"
                  onClick={routeToTaxForm}
                  className="btn btn--primary btn--full-mobile"
                  data-trackid="open-tax-form"
                >
                  {taxButtonText}
                </button>
              </div>
            )}
            {taxFormOnFile && !bankingOnFile && (
              <div id="banking">
                {editBankingMode ? (
                  <BankingDetails
                    showBillingHeader={false}
                    edit={true}
                    setShowBillingApiErrorAlert={setShowBillingApiErrorAlert}
                  />
                ) : (
                  <BankingDetails
                    showBillingHeader={false}
                    edit={false}
                    taxFormOnFile={taxFormOnFile}
                    setShowBillingApiErrorAlert={setShowBillingApiErrorAlert}
                  />
                )}
              </div>
            )}
            <div className="center-align">
              <div
                id="extendedWaiting"
                className="loader"
                aria-label="Please wait, it is loading"
              ></div>
              {!editMode && taxFormOnFile && bankingOnFile && (
                <p>Please wait...</p>
              )}
            </div>
            {editMode && (
              <div className="center-align">
                <button
                  type="button"
                  onClick={editTax}
                  className="btn btn--primary top-button-spacing button-min-width"
                  data-trackid="submit-new-w9"
                >
                  Submit New W-9
                </button>
                <button
                  type="button"
                  onClick={editBanking}
                  className="btn btn--primary button-min-width"
                  data-trackid="submit-new-bank-info"
                >
                  Submit New Bank Info
                </button>
              </div>
            )}
          </div>
        </div>
        {editMode && (
          <div className="center-align">
            <button
              type="button"
              onClick={() => {
                navigate("/home");
              }}
              className="btn btn--secondary"
              data-trackid="return-home"
            >
              Return Home
            </button>
          </div>
        )}
      </div>
  );
};

export default Onboarding;
